import React from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Checkbox, Grid, Typography, Button } from '@mui/material';

// Components
import { LineItem, SteamMapWidget } from '../../../../components';

// Constants
import { DATE_TIME_ZONE_FORMAT } from '../../../../constants';
import { SET_COLOR, HAUL_COLOR, STEAM_COLOR } from '../../../../components/SteamMapWidget';
import { HAUL } from '../../../../lib/TripHaul';
import { SET } from '../../../../lib/TripSet';

import humanizeDuration from 'humanize-duration';
import { Chart } from 'react-google-charts';
import FishCatchDialog from '../../../../components/FishCatchDialog';
import SetHaulShiftDialog from '../../../../components/SetHaulShiftDialog';

import { LBS_TO_KG, MILES_TO_KM } from '../../../../constants';
const TIME_PIE_CHART_OPTIONS = {
  legend: {
    position: 'bottom',
    alignment: 'center',
    textStyle: {
      fontSize: 14
    }
  },
  tooltip: {
    showColorCode: true,
  },
  chartArea: {
    left: 0,
    top: 10,
    width: '100%',
    height: '80%'
  },
  slices: [
    { color: STEAM_COLOR },
    { color: SET_COLOR },
    { color: HAUL_COLOR }
  ]
};

class OverviewView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showSteam: true,
      showSets: true,
      showHauls: true,
      isLoadingSets: false,
      isLoadingHauls: false,
      isLoadingSteam: false,
      isLoadingTrip: false,
      sets: [],
      hauls: [],
      steam: [],
      trip: null,
      selectedObject: null,
      fishCatch: [],
      showFishCatch: false,
      showSetHaulShift: false,
      tripStatistics: null
    };
  }

  onCheckboxChange = (event) => {
    this.setState({ [event.target.name]: event.target.checked });
  }

  clearSelection = () => {
    this.setState({ selectedObject: null });
  }

  onSetSelection = (setID) => {
    this.setState({ selectedObject: this.props.sets?.find(set => set.getID() === setID) });
  }

  onHaulSelection = (haulID) => {
    this.setState({ selectedObject: this.props.hauls?.find(haul => haul.getID() === haulID) });
  }

  displayDuration = (ms) => {
    return humanizeDuration(ms, { largest: 3, round: true, units: ['mo', 'w', 'd', 'h', 'm'] });
  }

  formatDurationForTooltip = (title, ms) => {
    return title + '\n' + this.displayDuration(ms);
  }

  getTimeSplitChart = () => {
    let steamTime = this.props.trip?.getStatistics()?.getElapsedSteamTime();
    let setTime = this.props.trip?.getSets()?.reduce((prev, current) => prev + (current?.getElapsedTime() ?? 0), 0);
    let haulTime = this.props.trip?.getSets()?.reduce((prev, current) => prev + (current?.getHaul()?.getElapsedTime() ?? 0), 0);

    let data = [['Type', 'Time', { role: 'tooltip', type: 'string' }],
      ['Steam', steamTime, this.formatDurationForTooltip('Steam', steamTime)],
      ['Set', setTime, this.formatDurationForTooltip('Set', setTime)],
      ['Haul', haulTime, this.formatDurationForTooltip('Haul', haulTime)]
    ];

    return (
      <Chart
        chartType="PieChart"
        data={data}
        options={TIME_PIE_CHART_OPTIONS}
        width={'100%'}
        height={'250px'}
        legend_toggle
      />
    );
  }

  tripInfo = () => {
    let gearAmount = 0;
    this.props.trip?.getSets()?.forEach(set => {
      gearAmount += set.getGearAmount() ?? 0;
    });

    // Calculate the Analyzed Haul Percentage (average of analyzed hauls and sets)
    let analyzedHaulCount = this.props.trip?.getSets()?.filter(set => set.getHaul()?.getIsAnalyzed())?.length ?? 0;
    let haulCount = this.props.trip?.getSets()?.filter(set => set.getHaul() != null)?.length ?? 0;
    let analyzedSetCount = this.props.trip?.getSets()?.filter(set => set.getIsAnalyzed())?.length ?? 0;
    let setCount = this.props.trip?.getSets()?.filter(set => set.getElapsedTime() != null)?.length ?? 0;
    let analyzedHaulPercentage = (haulCount > 0 && setCount > 0) ? (((analyzedHaulCount / haulCount) + (analyzedSetCount / setCount)) / 2) : null;
    return (
      <>
        <Typography variant='h4' style={{ textAlign: 'center' }}>Trip Summary</Typography>

        <LineItem
          value={this.props.trip?.getStartTimestamp() != null ? this.props.trip.getStartTimestamp().setZone(this.props.trip?.getTimezone())?.toLocaleString(DATE_TIME_ZONE_FORMAT) : 'Unavailable'}
          description={'Depart Date'}
        />

        <LineItem
          value={this.props.trip?.getStopTimestamp() != null ? this.props.trip.getStopTimestamp().setZone(this.props.trip?.getTimezone())?.toLocaleString(DATE_TIME_ZONE_FORMAT) : 'Unavailable'}
          description={'Return Date'}
        />

        {/** Total Trip Catch Viewer Here */}
        <LineItem
          value={
            <>
              <Typography>
                {this.props.trip?.getStatistics()?.getTotalRetainedLbs() != null ? (this.props.trip?.getConvertToKg() ? (
                  (this.props.trip?.getStatistics()?.getTotalRetainedLbs() * LBS_TO_KG)?.toFixed(1) + ' kg'
                ) : (
                  (this.props.trip?.getStatistics()?.getTotalRetainedLbs()?.toFixed(1)) + ' lbs'
                )) : 'Unavailable'}
              </Typography>
              <Button type='submit' disabled={this.state.isLoading || this.props.isLoadingTripCatch || this.props.catch == null || this.props.catch.length === 0} style={{ width: '100px' }} isLoading={this.props.isLoadingTripCatch} onClick={() =>
                this.setState({ fishCatchTitle: 'View Catch', showFishCatch: true, fishCatch: this.props.catch?.filter(c => c.getCatchType() == 'Retained' && c.getTripHaulID() == null) })
              }>
                View
              </Button>
            </>
          }
          description={'Total Target Catch'}
        />

        <LineItem
          value={
            <>
              <Typography>
                {this.props.trip?.getStatistics()?.getTotalDiscardLbs() != null ? (this.props.trip?.getConvertToKg() ? (
                  (this.props.trip?.getStatistics()?.getTotalDiscardLbs() * LBS_TO_KG)?.toFixed(1) + ' kg'
                ) : (
                  (this.props.trip?.getStatistics()?.getTotalDiscardLbs()?.toFixed(1)) + ' lbs'
                )) : 'Unavailable'}
              </Typography>
              <Button type='submit' disabled={this.state.isLoading || this.props.isLoadingTripCatch || this.props.catch == null || this.props.catch.length === 0} style={{ width: '100px' }} isLoading={this.props.isLoadingTripCatch} onClick={() =>
                this.setState({ fishCatchTitle: 'View Bycatch', showFishCatch: true, fishCatch: this.props.catch?.filter(c => c.getCatchType() == 'Discard' && c.getTripHaulID() == null) })
              }>
                View
              </Button>
            </>
          }
          description={'Total Bycatch'}
        />

        <LineItem
          value={this.props.trip?.getSets()?.filter(set => set.getHaul() != null)?.length ?? 'Unavailable'}
          description={'Total Hauls'}
        />

        <LineItem
          value={analyzedHaulPercentage != null ? (analyzedHaulPercentage * 100)?.toFixed(1) + '%' : 'Unavailable'}
          description={'Analyzed Haul Percentage'}
        />

        <Typography variant='h5' style={{ textAlign: 'center' }}>Fuel Efficiency</Typography>

        {this.props.trip?.getStatistics()?.getTotalDistance() != null &&
        <LineItem
          value = {
            this.props.trip?.getStatistics()?.getTotalDistance() != null ? (
              this.props.trip?.getConvertToKg() ? (
                (this.props.trip.getStatistics().getTotalDistance() * MILES_TO_KM).toFixed(1) + ' km'
              ) : (
                this.props.trip.getStatistics().getTotalDistance()?.toFixed(1) + ' miles'
              )
            ) : 'Unavailable'
          }
          description={'Trip Distance'}
        />}

        {/** Time Split Pie Chart here */}
        <LineItem
          value={this.props.trip?.getStatistics()?.getElapsedSteamTime() != null ? this.getTimeSplitChart() : 'Unavailable'}
          description={'Time Split'}
        />

        <LineItem
          value={this.props.trip?.getStatistics()?.getAverageSteamSpeed() != null ? (this.props.trip?.getStatistics()?.getAverageSteamSpeed()?.toFixed(1) + ' knots') : 'Unavailable'}
          description={'Average Steam Speed'}
        />

        <LineItem
          value={this.props.trip?.getStatistics()?.getAverageSetSpeed() != null ? (this.props.trip?.getStatistics()?.getAverageSetSpeed()?.toFixed(1) + ' knots') : 'Unavailable'}
          description={'Average Set Speed'}
        />

        <LineItem
          value={this.props.trip?.getStatistics()?.getAverageHaulSpeed() != null ? (this.props.trip?.getStatistics()?.getAverageHaulSpeed()?.toFixed(1) + ' knots') : 'Unavailable'}
          description={'Average Haul Speed'}
        />

        <Typography variant='h5' style={{ textAlign: 'center' }}>Catch Efficiency</Typography>

        {/** Catch lbs per drag minute or catch lbs per gear */}
        <LineItem
          value={gearAmount === 0 ? 'Unavailable' : (
            this.props.trip?.getStatistics()?.getRetainedLbsPerDragMinute() != null ? (
              this.props.trip?.getConvertToKg() ? (
                (this.props.trip?.getStatistics()?.getRetainedLbsPerDragMinute() * LBS_TO_KG)?.toFixed(1) + ' kg/min'
              ) : (
                this.props.trip?.getStatistics()?.getRetainedLbsPerDragMinute()?.toFixed(1) + ' lbs/min'
              )
            ) : (
              this.props.trip?.getConvertToKg() ? (
                (this.props.trip?.getStatistics()?.getRetainedLbsPerGear() * LBS_TO_KG)?.toFixed(1) + ' kg/gear'
              ) : (
                this.props.trip?.getStatistics()?.getRetainedLbsPerGear()?.toFixed(1) + ' lbs/gear'
              ) + (this.props.trip.getSets().length > 0 && this.props.trip.getSets()[0].getGearSubtype() != null ? ` (${this.props.trip.getSets()[0].getGearSubtype()})` : '')
            )
          )}
          description={'Target Catch Rate'}
        />

        {/** Discard lbs per drag minute or catch lbs per gear */}
        <LineItem
          value={gearAmount === 0 ? 'Unavailable' : (
            this.props.trip?.getStatistics()?.getDiscardLbsPerDragMinute() != null ? (
              this.props.trip?.getConvertToKg() ? (
                (this.props.trip?.getStatistics()?.getDiscardLbsPerDragMinute() * LBS_TO_KG)?.toFixed(1) + ' kg/min'
              ) : (
                this.props.trip?.getStatistics()?.getDiscardLbsPerDragMinute()?.toFixed(1) + ' lbs/min'
              )
            ) : (
              this.props.trip?.getConvertToKg() ? (
                (this.props.trip?.getStatistics()?.getDiscardLbsPerGear() * LBS_TO_KG)?.toFixed(1) + ' kg/gear'
              ) : (
                this.props.trip?.getStatistics()?.getDiscardLbsPerGear()?.toFixed(1) + ' lbs/gear'
              ) + (this.props.trip.getSets().length > 0 && this.props.trip.getSets()[0].getGearSubtype() != null ? ` (${this.props.trip.getSets()[0].getGearSubtype()})` : '')
            )
          )}
          description={'Bycatch Rate'}
        />

        {/** Catch lbs per processing hour */}
        <LineItem
          value={
            <>
              <Typography>
                {this.props.trip?.getStatistics()?.getCatchLbsPerProcessingHour() != null ? (this.props.trip?.getConvertToKg() ? (
                  (this.props.trip?.getStatistics()?.getCatchLbsPerProcessingHour() * LBS_TO_KG)?.toFixed(1) + ' kg/crewhour'
                ): (
                  this.props.trip?.getStatistics()?.getCatchLbsPerProcessingHour()?.toFixed(1) + ' lbs/crewhour'
                )) : 'Unavailable'}
              </Typography>
              <Button disabled={this.state.isLoading || this.props.trip?.getStatistics() == null} style={{ width: '100px' }} onClick={() =>
                this.setState({ showSetHaulShift: true, tripStatistics: this.props.trip?.getStatistics() })
              }>
                View
              </Button>
            </>
          }
          description={'Catch Processing Rate'}
        />

        <Typography variant='h5' style={{ textAlign: 'center' }}>Catch Quality</Typography>

        {/** average catch time on deck */}
        <LineItem
          value={this.props.trip?.getStatistics()?.getCatchQuality() != null ? humanizeDuration(this.props.trip?.getStatistics()?.getCatchQuality(), { round: true }) : 'Unavailable'}
          description={'Avg. Time On-Deck Predominate'}
        />
        <LineItem
          value={this.props.trip?.getStatistics()?.getCatchQualityNP() != null ? humanizeDuration(this.props.trip?.getStatistics()?.getCatchQualityNP(), { round: true }) : 'Unavailable'}
          description={'Avg. Time On-Deck Non-Predominate'}
        />
      </>
    );
  }

  selectedObjectInfo = () => {
    return (
      <>
        {/* Set Info */}
        {this.state.selectedObject != null && this.state.selectedObject?.getObjectType() === SET && (
          <>
            <Typography variant='h4' style={{ textAlign: 'center' }}>Set Detail</Typography>
            <LineItem
              value={this.state.selectedObject?.getStartTimestamp()?.setZone(this.props.trip?.getTimezone())?.toLocaleString(DATE_TIME_ZONE_FORMAT)}
              description={'Set Start'}
            />
            <LineItem
              value={this.state.selectedObject?.getElapsedTime() != null ? this.displayDuration(this.state.selectedObject?.getElapsedTime()) : null}
              description={'Set Time'}
            />
            {!this.state.selectedObject.getExcludeFromCalculations() &&
            <LineItem
              value={this.state.selectedObject?.getTotalDistance() != null ? (
                this.props.trip?.getConvertToKg() ? (
                  (this.state.selectedObject.getTotalDistance() * MILES_TO_KM).toFixed(1) + ' km'
                ) : (
                  this.state.selectedObject.getTotalDistance().toFixed(1) + ' miles'
                )
              ) : null}
              description={'Set Distance'}
            />}
            {!this.state.selectedObject.getExcludeFromCalculations() &&
            <LineItem
              value={this.state.selectedObject?.getAverageSpeed() != null ? (this.state.selectedObject?.getAverageSpeed()?.toFixed(1) + ' knots') : null}
              description={'Avg Set Speed'}
            />}
            {this.state.selectedObject?.getGearType() && <LineItem
              value={this.state.selectedObject?.getGearType()}
              description={'Gear Type'}
            />}
            {!this.state.selectedObject.getExcludeFromCalculations() && this.state.selectedObject?.getGearAmount() && <LineItem
              value={this.state.selectedObject?.getGearAmount()}
              description={'Gear Qty'}
            />}
            {this.state.selectedObject?.getGearSubtype() && <LineItem
              value={this.state.selectedObject?.getGearSubtype()}
              description={'Gear Subtype'}
            />}

            {this.state.selectedObject?.getDepth() && !this.state.selectedObject.getExcludeFromCalculations() && <LineItem
              value={this.state.selectedObject?.getDepth() + ' ft'}
              description={'Set Depth'}
            />}

            {this.state.selectedObject?.getIsAnalyzed() && !this.state.selectedObject.getExcludeFromCalculations() && this.state.selectedObject?.getCrewAmount() &&
            <LineItem
              value={this.state.selectedObject?.getCrewAmount()?.toFixed(1)}
              description={'Crewmen'}
            />}
          </>
        )}

        {/* Haul Info */}
        {this.state.selectedObject != null && this.state.selectedObject?.getObjectType() === HAUL && (
          <>
            <Typography variant='h4' style={{ textAlign: 'center' }}>Haul Detail</Typography>

            <LineItem
              value={this.state.selectedObject?.getStartTimestamp()?.setZone(this.props.trip?.getTimezone())?.toLocaleString(DATE_TIME_ZONE_FORMAT)}
              description={'Haul Start'}
            />

            <LineItem
              value={this.state.selectedObject?.getElapsedTime() != null ? this.displayDuration(this.state.selectedObject?.getElapsedTime()) : null}
              description={'Haul Time'}
            />

            {!this.state.selectedObject.getExcludeFromCalculations() &&
            <>
              <LineItem
                value={this.state.selectedObject?.getTotalDistance() != null ? (
                  this.props.trip?.getConvertToKg() ? (
                    (this.state.selectedObject.getTotalDistance() * MILES_TO_KM).toFixed(1) + ' km'
                  ) : (
                    this.state.selectedObject.getTotalDistance().toFixed(1) + ' miles'
                  )
                ): null}
                description={'Haul Distance'}
              />

              <LineItem
                value={this.state.selectedObject?.getAverageSpeed() != null ? this.state.selectedObject?.getAverageSpeed()?.toFixed(1) + ' knots' : null}
                description={'Avg Haul Speed'}
              />

              {this.state.selectedObject?.getRetainedLbs() != null && this.state.selectedObject?.getIsAnalyzed() &&
              <LineItem
                value={
                  <>
                    <Typography>
                      {this.state.selectedObject?.getRetainedLbs() == null ? 'Unavailable' : (
                        this.props.trip?.getConvertToKg() ? (
                          (this.state.selectedObject?.getRetainedLbs() * LBS_TO_KG)?.toFixed(1) + ' kg'
                        ) : (
                          this.state.selectedObject?.getRetainedLbs()?.toFixed(1) + ' lbs'
                        )
                      )}
                    </Typography>
                    <Button disabled={this.state.isLoading} style={{ width: '100px' }} onClick={() =>
                      this.setState({ fishCatchTitle: 'Catch', showFishCatch: true, fishCatch: this.state.selectedObject?.getCatch().filter(e => e.getCatchType() == 'Retained') })
                    }>
                      View
                    </Button>
                  </>
                }
                description={'Target Catch'}
              />}

              {/** Discard lbs here */}
              {this.state.selectedObject?.getDiscardLbs() != null && this.state.selectedObject?.getIsAnalyzed() &&
              <LineItem
                value={
                  <>
                    <Typography>
                      {this.state.selectedObject?.getDiscardLbs() == null ? 'Unavailable' : (
                        this.props.trip?.getConvertToKg() ? (
                          (this.state.selectedObject?.getDiscardLbs() * LBS_TO_KG)?.toFixed(1) + ' kg'
                        ) : (
                          this.state.selectedObject?.getDiscardLbs()?.toFixed(1) + ' lbs'
                        )
                      )}
                    </Typography>
                    <Button disabled={this.state.isLoading} style={{ width: '100px' }} onClick={() =>
                      this.setState({ fishCatchTitle: 'Bycatch', showFishCatch: true, fishCatch: this.state.selectedObject?.getCatch().filter(e => e.getCatchType() == 'Discard') })
                    }>
                      View
                    </Button>
                  </>
                }
                description={'Bycatch'}
              />}

              {this.state.selectedObject?.getIsAnalyzed() &&
              <LineItem
                value={this.state.selectedObject?.getRetainedLbsPerDragMinute() == null && this.state.selectedObject?.getRetainedLbsPerGear() == null ? 'Unavailable' : (
                  this.state.selectedObject?.getRetainedLbsPerDragMinute() != null ? (
                    this.props.trip?.getConvertToKg() ? (
                      (this.state.selectedObject?.getRetainedLbsPerDragMinute() * LBS_TO_KG)?.toFixed(1) + ' kg/min'
                    ) : (
                      this.state.selectedObject?.getRetainedLbsPerDragMinute()?.toFixed(1) + ' lbs/min'
                    )
                  ) : (
                    this.props.trip?.getConvertToKg() ? (
                      (this.state.selectedObject?.getRetainedLbsPerGear() * LBS_TO_KG)?.toFixed(1) + ' kg/gear'
                    ) : (
                      this.state.selectedObject?.getRetainedLbsPerGear()?.toFixed(1) + ' lbs/gear'
                    ) + (this.props.trip.getSets()?.length > 0 && this.props.trip.getSets()[0].getGearSubtype() != null ? ` (${this.props.trip.getSets()[0].getGearSubtype()})` : '')
                  )
                )}
                description={'Target Catch Rate'}
              />}

              {this.state.selectedObject?.getIsAnalyzed() &&
              <LineItem
                value={this.state.selectedObject?.getDiscardLbsPerDragMinute() == null && this.state.selectedObject?.getDiscardLbsPerGear() == null ? 'Unavailable' : (
                  this.state.selectedObject?.getDiscardLbsPerDragMinute() != null ? (
                    this.props.trip?.getConvertToKg() ? (
                      (this.state.selectedObject?.getDiscardLbsPerDragMinute() * LBS_TO_KG)?.toFixed(1) + ' kg/min'
                    ) : (
                      this.state.selectedObject?.getDiscardLbsPerDragMinute()?.toFixed(1) + ' lbs/min'
                    )
                  ) : (
                    this.props.trip?.getConvertToKg() ? (
                      (this.state.selectedObject?.getDiscardLbsPerGear() * LBS_TO_KG)?.toFixed(1) + ' kg/gear'
                    ) : (
                      this.state.selectedObject?.getDiscardLbsPerGear()?.toFixed(1) + ' lbs/gear'
                    ) + (this.props.trip.getSets()?.length > 0 && this.props.trip.getSets()[0].getGearSubtype() != null ? ` (${this.props.trip.getSets()[0].getGearSubtype()})` : '')
                  )
                )}
                description={'Bycatch Rate'}
              />}

              {this.state.selectedObject?.getIsAnalyzed() && this.state.selectedObject?.getCrewAmount() != null &&
              <LineItem
                value={this.state.selectedObject?.getCrewAmount()?.toFixed(1)}
                description={'Crewmen'}
              />}

              {this.state.selectedObject?.getIsAnalyzed() && this.state.selectedObject?.getCatchLbsPerProcessingHour() != null &&
              <LineItem
                value={this.state.selectedObject?.getCatchLbsPerProcessingHour() != null ? (
                  this.props.trip?.getConvertToKg() ? (
                    (this.state.selectedObject?.getCatchLbsPerProcessingHour() * LBS_TO_KG)?.toFixed(1) + ' kg/crewhour'
                  ) : (
                    this.state.selectedObject?.getCatchLbsPerProcessingHour()?.toFixed(1) + ' lbs/crewhour'
                  )
                ) : null}
                description={'Catch Processing Rate'}
              />}

              {/** Catch time-on-deck */}
              {this.state.selectedObject?.getIsAnalyzed() && this.state.selectedObject.getCatchQuality() != null &&
              <LineItem
                value={humanizeDuration(this.state.selectedObject.getCatchQuality(), { round: true })}
                description={'Avg. Time On-Deck Predominate'}
              />}

              {this.state.selectedObject?.getIsAnalyzed() && this.state.selectedObject.getCatchQualityNP() != null &&
              <LineItem
                value={humanizeDuration(this.state.selectedObject.getCatchQualityNP(), { round: true })}
                description={'Avg. Time On-Deck Non-Predominate'}
              />}
            </>}
          </>
        )}
      </>
    );
  }

  getDefaultCenter = () => {
    let center = { lng: 0, lat: 0 };
    if (this.props.steam?.length > 0) center = { lng: this.props.steam[0].getLongitude(), lat: this.props.steam[0].getLatitude() };
    else if (this.props.sets?.length > 0 && this.props.sets[0].getPositions()?.length > 0) center = { lng: this.props.sets[0].getPositions()[0].getLongitude(), lat: this.props.sets[0].getPositions()[0].getLatitude() };
    else if (this.props.hauls?.length > 0 && this.props.hauls[0].getPositions()?.length > 0) center = { lng: this.props.hauls[0].getPositions()[0].getLongitude(), lat: this.props.hauls[0].getPositions()[0].getLatitude() };
    return center;
  }

  render() {
    return (
      <div>

        <FishCatchDialog
          open={this.state.showFishCatch}
          onClose={() => this.setState({ showFishCatch: false, fishCatch: [] })}
          fishCatch={this.state.fishCatch}
          title={this.state.fishCatchTitle}
          convertToKg={this.props.trip?.getConvertToKg()}
          timezone={this.props.trip?.getTimezone()}
          onGoToImage={this.props.onGoToImage != null ? ((path) => {
            this.setState({ showFishCatch: false, fishCatch: [] });
            this.props.onGoToImage(path);
          }) : undefined}
        />

        <SetHaulShiftDialog
          open={this.state.showSetHaulShift}
          onClose={() => this.setState({ showSetHaulShift: false })}
          tripStatistics={this.state.tripStatistics}
        />

        <Grid container columnSpacing={3} rowSpacing={3}>
          <Grid item md={9} xs={12}>
            <SteamMapWidget
              width={'100%'}
              height={600}
              sets={this.state.showSets ? this.props.sets : []}
              hauls={this.state.showHauls ? this.props.hauls : []}
              steam={this.state.showSteam ? this.props.steam : []}
              clearSelection={this.clearSelection}
              onSetSelection={this.onSetSelection}
              onHaulSelection={this.onHaulSelection}
              selectedObject={this.state.selectedObject}
              defaultCenter={this.getDefaultCenter()}
            />
          </Grid>
          <Grid item md={3} xs={12}>
            <div style={{ width: '100%' }}>

              {/* Layer Selection */}
              <div style={{ border: '1px solid grey', padding: 20, borderRadius: 10, marginBottom: 10 }}>

                {/* Sets */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Checkbox
                    disabled={!this.props.showSets}
                    name='showSets'
                    defaultChecked={this.props.showSets ? this.state.showSets : false}
                    onChange={this.onCheckboxChange}
                    sx={{ color: SET_COLOR, '&.Mui-checked': { color: SET_COLOR }, opacity: this.props.showSets ? 1 : 0.5 }}
                  />
                  <Typography variant='body1'>
                    Sets
                    {this.props.isLoadingSets && <CircularProgress size={15} style={{ marginLeft: 10 }} />}
                  </Typography>
                </div>

                {/* Hauls */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Checkbox
                    name='showHauls'
                    defaultChecked={this.props.showHauls ? this.state.showHauls : false}
                    onChange={this.onCheckboxChange}
                    sx={{ color: HAUL_COLOR, '&.Mui-checked': { color: HAUL_COLOR }, opacity: this.props.showHauls ? 1 : 0.5 }}
                    disabled={!this.props.showHauls}
                  />
                  <Typography variant='body1'>
                    Hauls
                    {this.props.isLoadingHauls && <CircularProgress size={15} style={{ marginLeft: 10 }} />}
                  </Typography>
                </div>

                {/* Steam */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Checkbox
                    name='showSteam'
                    defaultChecked={this.props.showSteam ? this.state.showSteam : false}
                    onChange={this.onCheckboxChange}
                    sx={{ color: STEAM_COLOR, '&.Mui-checked': { color: STEAM_COLOR }, opacity: this.props.showSteam ? 1 : 0.5 }}
                    disabled={!this.props.showSteam}
                  />
                  <Typography variant='body1'>
                    Steam
                    {this.props.isLoadingSteam && <CircularProgress size={15} style={{ marginLeft: 10 }} />}
                  </Typography>
                </div>
              </div>

              {/* Information */}
              <Box sx={{ height: { xs: '100%', md: '400px' }, overflow: 'scroll', border: '1px solid grey', padding: '15px 20px 10px 20px', borderRadius: '10px' }}>
                {/* Trip Info */}

                <Box sx={{ display: 'block' }}>
                  {this.state.selectedObject == null && this.props.trip != null && this.tripInfo()}

                  {this.selectedObjectInfo()}
                </Box>
              </Box>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  }
}

OverviewView.propTypes = {
  trip: PropTypes.object.isRequired,
  isLoadingTripCatch: PropTypes.bool.isRequired,
  catch: PropTypes.array.isRequired,
  onGoToImage: PropTypes.func.isRequired,
  showSteam: PropTypes.bool,
  showSets: PropTypes.bool,
  showHauls: PropTypes.bool,
  isLoadingSets: PropTypes.bool,
  isLoadingHauls: PropTypes.bool,
  isLoadingSteam: PropTypes.bool,
  sets: PropTypes.array,
  hauls: PropTypes.array,
  steam: PropTypes.array,
};

export default OverviewView;
